import { SecondaryButton } from '@components/Buttons/SecondaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IUsuario } from '@model/Usuario';
import { IResetarSenhaRequest, useUsuarioService } from '@services/UsuarioService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { UsuarioResetarSenhaForm } from './UsuarioResetarSenhaForm';

export type UsuarioResetarSenhaModalProps = {
    modal: IModalWindow
    usuario: IUsuario
    onUsuarioAtualizado: (usuario: AxiosResponse<IUsuario>) => void
}

export function UsuarioResetarSenhaModal ({ modal, usuario, onUsuarioAtualizado }: UsuarioResetarSenhaModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const form = useForm<IResetarSenhaRequest>({
        novaSenha: useField(),
        confirmarNovaSenha: useField()
    })

    const validation = useValidation<IResetarSenhaRequest>({
        novaSenha: yup.string().trim().required().min(6),
        confirmarNovaSenha: yup.string().trim().required().min(6)
    });

    const usuarioService = useUsuarioService();

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    usuarioService.resetarSenha(usuario.usuarioId, values)
                        .then(response => {
                            message.success("Senha resetada com sucesso");
                            modal.close();
                            onUsuarioAtualizado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [form, loading, message, modal, onUsuarioAtualizado, usuario.usuarioId, usuarioService, validation]);

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
        >
            <ModalWindowTitle 
                title={`Reset de Senha do Usuário ${usuario.nome}`}
            />

            <ModalWindowContent>
                <UsuarioResetarSenhaForm 
                    form={form}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <SecondaryButton onClick={handleConfirm}>Resetar a Senha</SecondaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}